import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { StoryFormData, StoryType } from '../types/story';
import StoryTypeSelector from '../components/StoryWizard/StoryTypeSelector';
import StoryDetailsForm from '../components/StoryWizard/StoryDetailsForm';
import WordCountSelector from '../components/StoryWizard/WordCountSelector';
import ReviewConfirm from '../components/StoryWizard/ReviewConfirm';
import Logo from '../components/Logo';
import { api } from '../services/api';

export default function CreateStory() {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const updateUser = useAuthStore((state) => state.updateUser);

  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState<StoryFormData>({
    story_type: null,
    title: '',
    story_input: '',
    word_count: 2500,
  });

  const totalSteps = 4;

  // Step validation
  const isStepValid = (step: number): boolean => {
    switch (step) {
      case 1:
        return formData.story_type !== null;
      case 2:
        return formData.title.trim().length > 0 && formData.story_input.trim().length > 10;
      case 3:
        return true; // Word count always has a default
      case 4:
        return true; // Review step
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (currentStep < totalSteps && isStepValid(currentStep)) {
      setCurrentStep(currentStep + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleEdit = (step: number) => {
    setCurrentStep(step);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async () => {
    if (!formData.story_type) {
      alert('Please select a story type');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await api.createStory({
        title: formData.title,
        story_type: formData.story_type,
        story_input: formData.story_input,
        word_count: formData.word_count,
      });

      // Update user's credit balance
      if (response.new_credit_balance !== undefined) {
        updateUser({
          credit_balance: response.new_credit_balance,
        });
      }

      // Redirect to stories page immediately
      navigate('/stories');
    } catch (error) {
      console.error('Error creating story:', error);
      alert(
        error instanceof Error
          ? `Failed to create story: ${error.message}`
          : 'Failed to create story. Please try again.'
      );
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background-cream">
      {/* Header */}
      <header className="bg-white shadow-soft sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <Logo size="md" />
              <button
                onClick={() => navigate('/dashboard')}
                className="text-primary-600 hover:text-primary-700 font-semibold"
              >
                Dashboard
              </button>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm text-primary-500">Logged in as</p>
                <p className="text-emerald-600 font-semibold">{user?.email}</p>
              </div>
              <div className="px-4 py-2 bg-primary-50 rounded-lg border border-primary-200">
                <p className="text-sm text-primary-800">
                  Credits:{' '}
                  <span className="font-bold">{user?.credit_balance || 0}</span>
                </p>
              </div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mt-4">
            <div className="flex items-center gap-2">
              {Array.from({ length: totalSteps }).map((_, index) => (
                <div key={index} className="flex-1">
                  <div
                    className={`h-2 rounded-full transition-all duration-300 ${
                      index + 1 <= currentStep
                        ? 'bg-primary-500'
                        : 'bg-gray-200'
                    }`}
                  ></div>
                </div>
              ))}
            </div>
            <p className="text-sm text-gray-600 text-center mt-2">
              Step {currentStep} of {totalSteps}
            </p>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-3xl shadow-soft-lg p-8 md:p-12">
          {/* Step 1: Story Type Selection */}
          {currentStep === 1 && (
            <StoryTypeSelector
              selectedType={formData.story_type}
              onSelectType={(type: StoryType) =>
                setFormData({ ...formData, story_type: type })
              }
            />
          )}

          {/* Step 2: Story Details */}
          {currentStep === 2 && formData.story_type && (
            <StoryDetailsForm
              storyType={formData.story_type}
              title={formData.title}
              storyInput={formData.story_input}
              onTitleChange={(title) => setFormData({ ...formData, title })}
              onStoryInputChange={(input) =>
                setFormData({ ...formData, story_input: input })
              }
            />
          )}

          {/* Step 3: Word Count */}
          {currentStep === 3 && (
            <WordCountSelector
              selectedWordCount={formData.word_count}
              onSelectWordCount={(count) =>
                setFormData({ ...formData, word_count: count })
              }
              userCreditBalance={user?.credit_balance || 0}
            />
          )}

          {/* Step 4: Review & Confirm */}
          {currentStep === 4 && (
            <ReviewConfirm formData={formData} onEdit={handleEdit} />
          )}

          {/* Navigation Buttons */}
          <div className="mt-12 flex items-center justify-between">
            <button
              onClick={handleBack}
              disabled={currentStep === 1}
              className="px-6 py-3 text-text-medium hover:text-text-dark font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              Back
            </button>

            {currentStep < totalSteps ? (
              <button
                onClick={handleNext}
                disabled={!isStepValid(currentStep)}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Continue
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <svg
                      className="animate-spin h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Creating...
                  </span>
                ) : (
                  'Create Story ✨'
                )}
              </button>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
